import { useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';

import type {
  PublicSignerFieldResponse,
  PublicSignerSignatureValue
} from '../publicSignerTypes';

import { PublicSignerSignatureFieldInput } from './PublicSignerSignatureFieldInput';

export function PublicSignerSignatureDialog({
  error,
  field,
  initialValue,
  onClose,
  onSave,
  presetDrawnImageDataUrl,
  presetMode,
  presetTypedName
}: {
  error?: string;
  field: PublicSignerFieldResponse | null;
  initialValue: PublicSignerSignatureValue | null;
  onClose: () => void;
  onSave: (value: PublicSignerSignatureValue | null) => void;
  presetDrawnImageDataUrl: string | null;
  presetMode: 'typed' | 'drawn' | null;
  presetTypedName: string | null;
}) {
  const [draftValue, setDraftValue] =
    useState<PublicSignerSignatureValue | null>(initialValue);
  const lastFieldIdRef = useRef<string | null>(field?.id ?? null);

  // Sync reset on field change: useEffect would run after FieldInput
  // already mounted with stale draftValue, leaking substate into the new field.
  const currentFieldId = field?.id ?? null;
  if (lastFieldIdRef.current !== currentFieldId) {
    lastFieldIdRef.current = currentFieldId;
    setDraftValue(initialValue);
  }

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={Boolean(field)}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxWidth: '480px'
        }
      }}
    >
      <DialogTitle sx={{ px: 3, pt: 2, pb: 1 }}>
        {field?.label ?? 'Signature'}
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 0 }}>
        <Stack spacing={2} mt={0.5}>
          <Typography color="text.secondary" variant="body2">
            Choose a typed or drawn signature and apply it to the document.
          </Typography>
          <PublicSignerSignatureFieldInput
            key={field?.id}
            error={error}
            onChange={setDraftValue}
            presetDrawnImageDataUrl={presetDrawnImageDataUrl}
            presetMode={presetMode}
            presetTypedName={presetTypedName}
            value={draftValue}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={Boolean(field?.is_required) && !draftValue}
          onClick={() => {
            onSave(draftValue);
          }}
          variant="contained"
        >
          Apply signature
        </Button>
      </DialogActions>
    </Dialog>
  );
}
